import {Dispatch, SetStateAction, useEffect} from 'react';
import {OnNodesChange, useNodesState} from 'reactflow';

import type {
  ProcedureRunDesignFlowchartLayoutNode,
  ProcedureRunDesignFlowchartLayoutNodeData,
} from './useProcedureRunDesignFlowchartLayout';

export interface UseProcedureRunDesignFlowchartNodesArgs {
  initialColumnNodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  initialRows: Array<Array<ProcedureRunDesignFlowchartLayoutNode>>;
}

export interface UseProcedureRunDesignFlowchartNodesResult {
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  setNodes: Dispatch<SetStateAction<Array<ProcedureRunDesignFlowchartLayoutNode>>>;
  onNodesChange: OnNodesChange;
}

/**
 * Merges a freshly-built set of nodes with the current ReactFlow state. For each new node, reuse
 * the existing node's measured position/dimensions/visibility (refreshing only `data`) so an
 * already-laid-out flowchart isn't reset when conditions/methods change; brand-new nodes pass
 * through hidden at the origin so ReactFlow can measure them.
 */
function preserveMeasuredNodeState(
  currentNodes: Array<ProcedureRunDesignFlowchartLayoutNode>,
  newNodes: Array<ProcedureRunDesignFlowchartLayoutNode>
): Array<ProcedureRunDesignFlowchartLayoutNode> {
  const existingById = new Map(currentNodes.map((node) => [node.id, node]));

  return newNodes.map((newNode) => {
    const existing = existingById.get(newNode.id);
    return existing ? {...existing, data: newNode.data} : newNode;
  });
}

/**
 * Owns the ReactFlow node state for the procedure run design flowchart and keeps it in sync as
 * the underlying conditions/methods change.
 */
export function useProcedureRunDesignFlowchartNodes({
  initialColumnNodes,
  initialRows,
}: UseProcedureRunDesignFlowchartNodesArgs): UseProcedureRunDesignFlowchartNodesResult {
  const [nodes, setNodes, onNodesChange] = useNodesState<ProcedureRunDesignFlowchartLayoutNodeData>([
    ...initialColumnNodes,
    ...initialRows.flat(),
  ]);

  useEffect(() => {
    setNodes((currentNodes) =>
      preserveMeasuredNodeState(currentNodes, [...initialColumnNodes, ...initialRows.flat()])
    );
  }, [initialColumnNodes, initialRows, setNodes]);

  return {nodes, setNodes, onNodesChange};
}
